//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { InstagramReelsPostCustomizations } from '@bvengineering/universal-scheduling-gateway-service-types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PojoSet } from 'pojo-sets';
import { U } from 'ts-toolbelt';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import {
  IInstagramReelsAutomaticPostConfig,
  IInstagramReelsManualPostConfig,
} from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { IDraftInstagramPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import {
  IComparableInstagramReelsPostDetails,
  IInstagramReelsPostDetailsState,
  InstagramReelsCreativeNotesType,
  IReelsCreativeNote,
} from '@instagram-account/redux/instagram-reels-post-details/instagramReelsPostDetailsModel';
import { validateInstagramReelsPostFields } from '@instagram-account/redux/instagram-reels-post-details/instagramReelsPostDetailsUtils';
import { InstagramPostDetailsValidationError } from '@instagram-account/redux/post-details/instagramPostDetailsModel';
import { createDefaultCaption } from '@instagram-account/redux/post-details/instagramPostDetailsUtils';
import { getPublishingStrategyAndInfoText } from '@instagram-account/redux/post-details/selectors/get-publishing-strategy/getPublishingStrategyAndInfoText';
import {
  getCurrentReelsCaption,
  getCurrentReelsCustomizations,
  getCurrentReelsOther,
  getCurrentReelsStickers,
  getCurrentReelsTextOverlay,
} from '@instagram-account/redux/post-details/selectors/getCurrentReelsPostDetails';
import { getCurrentSelectedReelsCuid } from '@instagram-account/redux/post-details/selectors/getCurrentSelectedCuids';
import { getInstagramStandardPostDetails } from '@instagram-account/redux/schedule/instagramScheduleUtils';
import Cuid from '@models/Cuid';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getScheduleMediaMap } from '@redux/schedule-media/getScheduleMediaMap';
import * as scheduleMediaActions from '@redux/schedule-media/scheduleMediaSlice';
import { getClientId } from '@redux/selectors/getClientId';
import { isDefined } from '@utils/filterPredicates';

const initialState: IInstagramReelsPostDetailsState = {
  currentReelsPostDetails: {
    cuid: undefined,
    customizations: {},
    shareToFeed: true,
  },
  initialReelsPostDetails: {
    cuid: undefined,
    customizations: {},
    shareToFeed: true,
  },

  flags: {
    isFetchingSelectedMedia: false,
  },

  validationErrors: PojoSet.empty<InstagramPostDetailsValidationError>(),
};

const { reducer: InstagramReelsPostDetailsReducer, actions } = createSlice({
  name: SliceNames.InstagramAccountReelsPostDetails,
  initialState,
  reducers: {
    setInitialReelsPostDetails(
      state,
      {
        payload: instagramReelsPostDetails,
      }: PayloadAction<Partial<IComparableInstagramReelsPostDetails>>,
    ) {
      state.initialReelsPostDetails = {
        ...initialState.initialReelsPostDetails,
        ...instagramReelsPostDetails,
      };
      state.currentReelsPostDetails = {
        ...initialState.currentReelsPostDetails,
        ...instagramReelsPostDetails,
      };
    },
    setSelectedCuid(state, { payload: cuid }: PayloadAction<Cuid>) {
      state.currentReelsPostDetails.cuid = cuid;
    },
    setCustomizations(
      state,
      {
        payload: customizations,
      }: PayloadAction<InstagramReelsPostCustomizations>,
    ) {
      state.currentReelsPostDetails.customizations = {
        ...state.currentReelsPostDetails.customizations,
        ...customizations,
      };
    },
    setShareToFeed(state, { payload: shareToFeed }: PayloadAction<boolean>) {
      state.currentReelsPostDetails.shareToFeed = shareToFeed;
    },
    setValidationErrors(
      state,
      {
        payload: validationErrors,
      }: PayloadAction<PojoSet<InstagramPostDetailsValidationError>>,
    ) {
      state.validationErrors = validationErrors;
    },
    requestSelectedMedia(state) {
      // Reset the NoMedia validation error
      const validationErrors = PojoSet.remove(
        state.validationErrors,
        InstagramPostDetailsValidationError.NoMedia,
      );

      state.flags.isFetchingSelectedMedia = true;
      state.validationErrors = validationErrors;
    },
    receiveSelectedMediaSucceeded(state) {
      state.flags.isFetchingSelectedMedia = false;
    },
    receiveSelectedMediaFailed(state) {
      state.flags.isFetchingSelectedMedia = false;
    },
    removeAllCustomizations(state) {
      state.currentReelsPostDetails.customizations = {};
    },
    removeSelectedCuid(state) {
      // Reset any media validation errors
      const validationErrors = PojoSet.difference(
        state.validationErrors,
        PojoSet.from([
          InstagramPostDetailsValidationError.InvalidMediaAspectRatio,
          InstagramPostDetailsValidationError.InvalidReelsVideoSize,
          InstagramPostDetailsValidationError.InvalidVideoLength,
        ]),
      );

      state.currentReelsPostDetails.cuid = undefined;
      state.validationErrors = validationErrors;
    },
    resetReelsPostDetails: () => initialState,
  },
});

export const {
  removeAllCustomizations,
  removeSelectedCuid,
  resetReelsPostDetails,
  setCustomizations,
  setShareToFeed,
  setValidationErrors,
} = actions;

export const fetchAndSetSelectedMedia = createThunk(
  (item: IContentLibraryItem) => async (dispatch, getState) => {
    const clientId = getClientId(getState());
    const newCuid = Cuid.build(item, clientId);
    const currentCaption = getCurrentReelsCaption(getState());

    dispatch(actions.requestSelectedMedia());

    try {
      const mediaItem = await dispatch(
        scheduleMediaActions.getMediaItemByCuid(newCuid),
      );

      // Only set a default caption if there is not an existing caption
      if (!currentCaption) {
        const defaultCaption = createDefaultCaption(mediaItem);

        dispatch(setCaption(defaultCaption ?? ''));
      }

      dispatch(actions.receiveSelectedMediaSucceeded());
    } catch (e) {
      dispatch(actions.receiveSelectedMediaFailed());
    }

    dispatch(actions.setSelectedCuid(newCuid));
  },
);

export const setUpCreateReelsPost = createThunk(
  (cuid: Cuid, defaultCaption?: string) => (dispatch, getState) => {
    const currentCaption = getCurrentReelsCaption(getState());

    dispatch(
      actions.setInitialReelsPostDetails({
        cuid,
        customizations: {
          // Only set a default caption if there is not an existing caption
          // The current path to this method would not result in having a currentCaption
          // but this is here to ensure we don't overwrite it if that changes in the future
          ...(!currentCaption && defaultCaption && { caption: defaultCaption }),
        },
      }),
    );
  },
);

export const setUpEditReelsPost = createThunk(
  (
    scheduledReelsPostConfig: U.Strict<
      IInstagramReelsManualPostConfig | IInstagramReelsAutomaticPostConfig
    >,
  ) =>
    (dispatch) => {
      const { cuids, customizations, shareToFeed } = scheduledReelsPostConfig;
      const cuid = cuids[0];

      dispatch(
        actions.setInitialReelsPostDetails({
          cuid,
          customizations,
          shareToFeed,
        }),
      );
    },
);

export const setUpEditDraftReelsPost = createThunk(
  (draft: IDraftInstagramPost) => (dispatch) => {
    const { previewMediaItem, customizations, shareToFeed } =
      getInstagramStandardPostDetails(draft);

    dispatch(
      actions.setInitialReelsPostDetails({
        cuid: previewMediaItem?.cuid,
        customizations,
        shareToFeed,
      }),
    );
  },
);

export const addCreativeNoteType = createThunk(
  (noteType: InstagramReelsCreativeNotesType) => (dispatch, getState) => {
    switch (noteType) {
      case 'audio': {
        dispatch(setAudio(''));
        return;
      }
      case 'effects': {
        dispatch(setEffects(''));
        return;
      }
      case 'other': {
        const currentOtherNotes = getCurrentReelsOther(getState()) ?? [];
        dispatch(setOtherNotes([...currentOtherNotes, '']));
        return;
      }
      case 'stickers': {
        const currentStickers = getCurrentReelsStickers(getState()) ?? [];
        dispatch(setStickers([...currentStickers, '']));
        return;
      }
      case 'textOverlay': {
        const currentTextOverlay = getCurrentReelsTextOverlay(getState()) ?? [];
        dispatch(setTextOverlay([...currentTextOverlay, '']));
        return;
      }
    }
  },
);

export const updateCreativeNote = createThunk(
  (creativeNote: IReelsCreativeNote) => (dispatch, getState) => {
    switch (creativeNote.type) {
      case 'audio': {
        dispatch(setAudio(creativeNote.value));
        return;
      }
      case 'effects': {
        dispatch(setEffects(creativeNote.value));
        return;
      }
      case 'other': {
        const currentOtherNotes = getCurrentReelsOther(getState()) ?? [];
        dispatch(
          setOtherNotes(
            currentOtherNotes.map((note, index) => {
              if (
                isDefined(creativeNote.index) &&
                creativeNote.index === index
              ) {
                return creativeNote.value;
              }

              return note;
            }),
          ),
        );

        return;
      }
      case 'stickers': {
        const currentStickers = getCurrentReelsStickers(getState()) ?? [];
        dispatch(
          setStickers(
            currentStickers.map((note, index) => {
              if (
                isDefined(creativeNote.index) &&
                creativeNote.index === index
              ) {
                return creativeNote.value;
              }

              return note;
            }),
          ),
        );

        return;
      }
      case 'textOverlay': {
        const currentTextOverlay = getCurrentReelsTextOverlay(getState()) ?? [];
        dispatch(
          setTextOverlay(
            currentTextOverlay.map((note, index) => {
              if (
                isDefined(creativeNote.index) &&
                creativeNote.index === index
              ) {
                return creativeNote.value;
              }

              return note;
            }),
          ),
        );

        return;
      }
    }
  },
);

export const removeCreativeNote = createThunk(
  (noteType: InstagramReelsCreativeNotesType, noteIndex?: number) =>
    (dispatch, getState) => {
      switch (noteType) {
        case 'audio': {
          dispatch(setAudio(undefined));
          return;
        }
        case 'effects': {
          dispatch(setEffects(undefined));
          return;
        }
        case 'other': {
          const currentOtherNotes = getCurrentReelsOther(getState()) ?? [];
          dispatch(
            setOtherNotes(
              currentOtherNotes.filter((_note, index) => {
                return noteIndex !== index;
              }),
            ),
          );

          return;
        }
        case 'stickers': {
          const currentStickers = getCurrentReelsStickers(getState()) ?? [];
          dispatch(
            setStickers(
              currentStickers.filter((_note, index) => {
                return noteIndex !== index;
              }),
            ),
          );

          return;
        }
        case 'textOverlay': {
          const currentTextOverlay =
            getCurrentReelsTextOverlay(getState()) ?? [];
          dispatch(
            setTextOverlay(
              currentTextOverlay.filter((_note, index) => {
                return noteIndex !== index;
              }),
            ),
          );

          return;
        }
      }
    },
);

export const setAudio = createThunk(
  (audio: string | undefined) => (dispatch) => {
    dispatch(actions.setCustomizations({ audio }));
  },
);

export const setCaption = createThunk(
  (caption: string) => (dispatch, getState) => {
    dispatch(actions.setCustomizations({ caption }));

    const currentErrors =
      getState().instagram.postDetails.reels.validationErrors;

    // Reset caption validation errors
    const validationErrors = PojoSet.difference(
      currentErrors,
      PojoSet.from([
        InstagramPostDetailsValidationError.CaptionTooLong,
        InstagramPostDetailsValidationError.TooManyHashtags,
      ]),
    );

    dispatch(actions.setValidationErrors(validationErrors));
  },
);

export const setEffects = createThunk(
  (effects: string | undefined) => (dispatch) => {
    dispatch(actions.setCustomizations({ effects }));
  },
);

export const setLocation = createThunk((location: string) => (dispatch) => {
  dispatch(actions.setCustomizations({ location }));
});

export const setOtherNotes = createThunk(
  (otherNotes: string[]) => (dispatch) => {
    dispatch(actions.setCustomizations({ other: otherNotes }));
  },
);

export const setStickers = createThunk((stickers: string[]) => (dispatch) => {
  dispatch(actions.setCustomizations({ stickers }));
});

export const setTextOverlay = createThunk(
  (textOverlay: string[]) => (dispatch) => {
    dispatch(actions.setCustomizations({ textOverlay }));
  },
);

export const setUserTags = createThunk((userTags: string) => (dispatch) => {
  dispatch(actions.setCustomizations({ userTags }));
});

// Removes any customizations set to an empty string
export const cleanupReelsCustomizations = createThunk(
  () => (dispatch, getState) => {
    const customizations = getCurrentReelsCustomizations(getState());
    const customizationsKeys = Object.keys(
      customizations,
    ) as (keyof typeof customizations)[];
    const cleanCustomizations: InstagramReelsPostCustomizations = {
      ...customizations,
    };

    function setStringProp<T extends {}, K extends keyof T>(
      obj: T,
      key: K,
      val: string,
    ) {
      const m: Partial<Record<K, string>> =
        typeof obj[key] === 'string' ? obj : {};
      m[key] = val;
    }
    function setArrayProp<T extends {}, K extends keyof T>(
      obj: T,
      key: K,
      val: string[],
    ) {
      const m: Partial<Record<K, string[]>> = Array.isArray(obj[key])
        ? obj
        : {};
      m[key] = val;
    }

    customizationsKeys.forEach((key) => {
      const items = customizations[key];
      if (isDefined(items)) {
        if (Array.isArray(items)) {
          const filteredItems = items.filter(
            (item) => isDefined(item) && item !== '',
          );

          if (filteredItems.length) {
            setArrayProp(cleanCustomizations, key, filteredItems);
          } else {
            cleanCustomizations[key] = undefined;
          }
        }

        if (typeof items === 'string') {
          if (items !== '') {
            setStringProp(cleanCustomizations, key, items);
          } else {
            cleanCustomizations[key] = undefined;
          }
        }
      }
    });

    dispatch(setCustomizations(cleanCustomizations));
  },
);

export const clearAllCustomizationsExceptCaption = createThunk(
  () => (dispatch, getState) => {
    const caption = getCurrentReelsCaption(getState());

    dispatch(removeAllCustomizations());
    dispatch(setCustomizations({ caption }));
  },
);

export const validateReelsPostDetails = createThunk(
  () =>
    (dispatch, getState): boolean => {
      const cuid = getCurrentSelectedReelsCuid(getState());
      const caption = getCurrentReelsCaption(getState());
      const scheduleMediaMap = getScheduleMediaMap(getState());
      const { validatedPublishingStrategy } =
        getPublishingStrategyAndInfoText(getState());

      const reelsPostErrors = validateInstagramReelsPostFields({
        scheduleMediaMap,
        publishingStrategy: validatedPublishingStrategy,
        cuid,
        caption,
      });

      dispatch(setValidationErrors(reelsPostErrors));

      return PojoSet.size(reelsPostErrors) === 0;
    },
);

export default InstagramReelsPostDetailsReducer;
